(() => {
  const STORAGE_KEY = 'pfgNativeQuizState';
  const SCREEN_ORDER = ['intro', 'age', 'concern', 'skinType', 'restrictions', 'area', 'email'];
  const QUESTION_SCREENS = ['age', 'concern', 'skinType', 'restrictions', 'area'];
  const RESULT_QUERY_PARAM = 'result';
  const VALID_RESULT_IDS = new Set(['1', '2', '3', '4', '5', '6', '7', '8', '9']);
  const initializedRoots = new WeakSet();

  function isThemeEditor() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function normalizeResultId(value) {
    const resultId = String(value || '').trim();
    return VALID_RESULT_IDS.has(resultId) ? resultId : null;
  }

  function buildResultUrl(resultId) {
    const validResultId = normalizeResultId(resultId);
    const url = new URL(window.location.href);

    if (validResultId) {
      url.searchParams.set(RESULT_QUERY_PARAM, validResultId);
    } else {
      url.searchParams.delete(RESULT_QUERY_PARAM);
    }

    url.hash = '';
    return url.toString();
  }

  function updateResultUrl(resultId) {
    if (isThemeEditor() || !window.history || !window.history.replaceState) return;

    const nextUrl = buildResultUrl(resultId);
    window.history.replaceState(
      { ...(window.history.state || {}), pfgQuizResult: normalizeResultId(resultId) },
      '',
      nextUrl
    );
  }

  function getResultIdFromUrl() {
    if (isThemeEditor()) return null;

    try {
      const url = new URL(window.location.href);
      const rawResultId = url.searchParams.get(RESULT_QUERY_PARAM);

      if (!rawResultId) return null;

      const validResultId = normalizeResultId(rawResultId);

      if (!validResultId) {
        updateResultUrl(null);
        console.warn('[PFG Quiz] Ignored invalid result URL parameter:', rawResultId);
        return null;
      }

      return validResultId;
    } catch (error) {
      console.warn('[PFG Quiz] Could not read the result URL parameter.', error);
      return null;
    }
  }

  function createDefaultState() {
    return {
      age: null,
      concern: null,
      skinType: null,
      restrictions: [],
      area: null,
      email: '',
      emailCaptured: false,
      emailSkipped: false,
      currentScreen: 'intro'
    };
  }

  function loadState() {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (!saved) return createDefaultState();

      const parsed = JSON.parse(saved);
      return {
        ...createDefaultState(),
        ...parsed,
        restrictions: Array.isArray(parsed.restrictions) ? parsed.restrictions : []
      };
    } catch (error) {
      console.warn('[PFG Quiz] Could not restore quiz state.', error);
      return createDefaultState();
    }
  }

  function saveState(state) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn('[PFG Quiz] Could not save quiz state.', error);
    }
  }

  function clearState() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('[PFG Quiz] Could not clear quiz state.', error);
    }
  }

  function determineQuizResult(answers) {
    const { age, concern, area } = answers;

    if (area === 'body') return '6';
    if (area === 'eyes') return '7';
    if (area === 'hair') return '8';
    if (area === 'targeted') return '9';

    if (area !== 'face') {
      throw new Error('Please choose where you need the most support.');
    }

    if (concern === 'dryness') return '1';
    if (concern === 'redness') return '2';
    if (concern === 'acne') return '3';

    if (concern === 'firmness') {
      if (!age) {
        throw new Error('Please choose your age group.');
      }

      return ['18-25', '26-39'].includes(age) ? '4' : '5';
    }

    throw new Error('Please choose your biggest skin concern.');
  }

  function initQuiz(root) {
    if (!root || initializedRoots.has(root)) return;
    initializedRoots.add(root);

    const state = loadState();
    const screens = Array.from(root.querySelectorAll('[data-quiz-screen]'));
    const message = root.querySelector('[data-quiz-message]');
    const backButton = root.querySelector('[data-quiz-back]');
    const progressWrap = root.querySelector('[data-quiz-progress-wrap]');
    const progressLabel = root.querySelector('[data-quiz-progress-label]');
    const progressPercent = root.querySelector('[data-quiz-progress-percent]');
    const progressBar = root.querySelector('[data-quiz-progress-bar]');
    const progress = root.querySelector('.pfg-quiz__progress');
    const emailForm = root.querySelector('[data-quiz-email-form]');
    const emailInput = root.querySelector('[data-quiz-email-input]');
    const emailSubmit = root.querySelector('[data-quiz-email-submit]');
    const emailStatus = root.querySelector('[data-quiz-email-status]');
    const klaviyoPublicKey = String(root.dataset.klaviyoPublicKey || '').trim();
    const klaviyoListId = String(root.dataset.klaviyoListId || '').trim();
    const klaviyoSource = String(root.dataset.klaviyoSource || 'PFG Native Skincare Quiz').trim();
    const progressCompleteLabel = String(
      root.dataset.progressCompleteLabel || 'Your result is ready'
    ).trim();
    const emailSubmitLabel = String(
      root.dataset.emailSubmitLabel || 'Send my results'
    ).trim();
    const emailSavingLabel = String(
      root.dataset.emailSavingLabel || 'Saving…'
    ).trim();

    function getScreen(name) {
      return screens.find((screen) => screen.dataset.quizScreen === name) || null;
    }

    function showMessage(text) {
      if (!message) return;
      message.textContent = text;
      message.hidden = !text;
    }

    function clearMessage() {
      showMessage('');
    }

    function syncAnswerUI() {
      root.querySelectorAll('[data-quiz-answer]').forEach((button) => {
        const question = button.dataset.question;
        const value = button.dataset.value;
        const selected = question === 'restrictions'
          ? state.restrictions.includes(value)
          : state[question] === value;

        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    function screenIsComplete(screenName) {
      if (screenName === 'intro' || screenName === 'email') return true;
      if (screenName === 'restrictions') return state.restrictions.length > 0;
      return Boolean(state[screenName]);
    }

    function syncCurrentButtonState() {
      const current = getScreen(state.currentScreen);
      if (!current) return;

      const actionButton = current.querySelector('[data-quiz-next], [data-quiz-submit]');
      if (actionButton) {
        actionButton.disabled = !screenIsComplete(state.currentScreen);
      }

      if (emailSubmit && state.currentScreen === 'email') {
        emailSubmit.disabled = !isValidEmail(emailInput ? emailInput.value : state.email);
      }
    }

    function updateProgress() {
      const isIntro = state.currentScreen === 'intro';
      if (progressWrap) progressWrap.hidden = isIntro;
      if (backButton) backButton.hidden = isIntro;
      if (isIntro) return;

      const isEmailScreen = state.currentScreen === 'email';
      const questionIndex = QUESTION_SCREENS.indexOf(state.currentScreen);
      const step = isEmailScreen
        ? QUESTION_SCREENS.length
        : Math.max(1, questionIndex + 1);
      const percent = Math.round((step / QUESTION_SCREENS.length) * 100);

      if (progressLabel) {
        progressLabel.textContent = isEmailScreen
          ? progressCompleteLabel
          : `${step} of ${QUESTION_SCREENS.length}`;
      }
      if (progressPercent) progressPercent.textContent = `${percent}%`;
      if (progressBar) progressBar.style.width = `${percent}%`;
      if (progress) progress.setAttribute('aria-valuenow', String(percent));
    }

    function showScreen(name, options = {}) {
      const { focus = true, persist = true } = options;
      const target = getScreen(name);
      if (!target) return;

      screens.forEach((screen) => {
        const isTarget = screen === target;
        screen.hidden = !isTarget;
        screen.classList.toggle('is-active', isTarget);
      });

      state.currentScreen = name;
      if (persist) saveState(state);

      clearMessage();
      syncAnswerUI();

      if (name === 'email' && emailInput && state.email && !emailInput.value) {
        emailInput.value = state.email;
      }

      syncCurrentButtonState();
      updateProgress();

      if (focus) {
        requestAnimationFrame(() => {
          const heading = target.querySelector('h1, h2');
          if (heading) {
            heading.setAttribute('tabindex', '-1');
            heading.focus({ preventScroll: true });
          }

          root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }

    function moveForward() {
      if (!screenIsComplete(state.currentScreen)) {
        showMessage('Please select an answer before continuing.');
        return;
      }

      const currentIndex = SCREEN_ORDER.indexOf(state.currentScreen);
      const nextScreen = SCREEN_ORDER[currentIndex + 1];
      if (nextScreen) showScreen(nextScreen);
    }

    function moveBack() {
      const currentIndex = SCREEN_ORDER.indexOf(state.currentScreen);
      const previousScreen = SCREEN_ORDER[Math.max(0, currentIndex - 1)];
      showScreen(previousScreen);
    }

    function chooseSingleAnswer(button) {
      const question = button.dataset.question;
      const value = button.dataset.value;
      state[question] = value;
      saveState(state);
      syncAnswerUI();
      syncCurrentButtonState();
      clearMessage();
    }

    function chooseRestriction(button) {
      const value = button.dataset.value;
      const current = new Set(state.restrictions);

      if (value === 'none') {
        state.restrictions = current.has('none') ? [] : ['none'];
      } else {
        current.delete('none');
        current.has(value) ? current.delete(value) : current.add(value);
        state.restrictions = Array.from(current);
      }

      saveState(state);
      syncAnswerUI();
      syncCurrentButtonState();
      clearMessage();
    }


    function isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
    }

    function setEmailStatus(text, type = '') {
      if (!emailStatus) return;

      emailStatus.textContent = text;
      emailStatus.hidden = !text;
      emailStatus.dataset.status = type;
    }

    function getResultTitle(resultId) {
      const result = Array.from(
        document.querySelectorAll('[data-quiz-result]')
      ).find((item) => item.dataset.quizResult === String(resultId));

      if (!result) return `Result ${resultId}`;

      const title = result.querySelector('.jd-qk-result-hero__result');
      if (!title) return `Result ${resultId}`;

      const cleanTitle = title.cloneNode(true);

      cleanTitle
        .querySelectorAll('.jd-qk-result-hero__sparkle, svg')
        .forEach((element) => element.remove());

      return cleanTitle.textContent.replace(/\s+/g, ' ').trim();
    }

    function getQuizProperties(resultId) {
      const resultTitle = getResultTitle(resultId);

      return {
        'PFG Quiz Result': resultTitle,
        'PFG Quiz Result URL': buildResultUrl(resultId),
        'PFG Quiz Area': state.area || '',
        'PFG Quiz Concern': state.concern || '',
        'PFG Quiz Age': state.age || '',
        'PFG Quiz Skin Type': state.skinType || '',
        'PFG Quiz Restrictions': Array.isArray(state.restrictions)
          ? state.restrictions
          : [],
        'PFG Quiz Source': klaviyoSource,
        'PFG Quiz Completed At': new Date().toISOString()
      };
    }

    function identifyWithKlaviyo(email, properties) {
      try {
        if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
          window.klaviyo.identify({
            email,
            ...properties
          });
          return;
        }

        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push([
          'identify',
          {
            '$email': email,
            ...properties
          }
        ]);
      } catch (error) {
        console.warn('[PFG Quiz] Klaviyo identify call failed.', error);
      }
    }

    async function subscribeToKlaviyo(email, resultId) {
      const properties = getQuizProperties(resultId);
      identifyWithKlaviyo(email, properties);

      if (!klaviyoPublicKey) {
        console.warn(
          '[PFG Quiz] Email captured locally, but the Klaviyo public API key is blank.'
        );
        return {
          subscribed: false,
          reason: 'missing-public-key'
        };
      }

      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: klaviyoSource,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  subscriptions: {
                    email: {
                      marketing: {
                        consent: 'SUBSCRIBED'
                      }
                    }
                  },
                  properties
                }
              }
            }
          }
        }
      };

      if (klaviyoListId) {
        payload.data.relationships = {
          list: {
            data: {
              type: 'list',
              id: klaviyoListId
            }
          }
        };
      }

      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(klaviyoPublicKey)}`,
        {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: {
            accept: 'application/vnd.api+json',
            'content-type': 'application/vnd.api+json',
            revision: '2026-07-15'
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        let details = '';

        try {
          details = await response.text();
        } catch (error) {
          details = '';
        }

        throw new Error(
          `Klaviyo subscription failed (${response.status}). ${details}`.trim()
        );
      }

      return {
        subscribed: true
      };
    }

    function hideAllResults() {
      document.querySelectorAll('[data-quiz-result]').forEach((result) => {
        result.hidden = true;
        result.classList.remove('is-active');
      });
    }

    function showResult(resultId, options = {}) {
      const {
        updateUrl = true,
        focus = true,
        dispatchCompleted = true
      } = options;

      const validResultId = normalizeResultId(resultId);

      if (!validResultId) {
        throw new Error(`Result ${resultId} is not a valid quiz result.`);
      }

      const results = Array.from(document.querySelectorAll('[data-quiz-result]'));
      const target = results.find(
        (result) => result.dataset.quizResult === validResultId
      );

      if (!target) {
        throw new Error(`Result ${validResultId} is missing from the page template.`);
      }

      results.forEach((result) => {
        const active = result === target;
        result.hidden = !active;
        result.classList.toggle('is-active', active);
      });

      root.hidden = true;
      state.resultId = validResultId;
      saveState(state);

      document.documentElement.dataset.pfgQuizResult = validResultId;

      if (updateUrl) {
        updateResultUrl(validResultId);
      }

      document.dispatchEvent(new CustomEvent('pfg:quiz-result-rendered', {
        detail: {
          resultId: validResultId,
          resultUrl: buildResultUrl(validResultId),
          root: target
        }
      }));

      if (focus) {
        requestAnimationFrame(() => {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          const heading = target.querySelector('h1, h2');
          if (heading) {
            heading.setAttribute('tabindex', '-1');
            heading.focus({ preventScroll: true });
          }
        });
      }

      if (dispatchCompleted) {
        document.dispatchEvent(new CustomEvent('pfg:quiz:completed', {
          detail: {
            resultId: validResultId,
            resultUrl: buildResultUrl(validResultId),
            resultTitle: getResultTitle(validResultId),
            answers: { ...state }
          }
        }));
      }
    }

    function resolveResultId() {
      if (!screenIsComplete('area')) {
        throw new Error('Please choose where you need the most support.');
      }

      const resultId = determineQuizResult(state);
      state.resultId = resultId;
      saveState(state);
      return resultId;
    }

    function submitQuiz() {
      try {
        const resultId = resolveResultId();
        showResult(resultId);
      } catch (error) {
        console.error('[PFG Quiz]', error);
        showMessage(
          error.message ||
          'We could not determine your result. Please review your answers.'
        );
      }
    }

    async function submitEmailCapture() {
      const email = String(emailInput ? emailInput.value : '').trim();

      if (!isValidEmail(email)) {
        setEmailStatus('Please enter a valid email address.', 'error');
        if (emailInput) emailInput.focus();
        return;
      }

      let resultId;

      try {
        resultId = resolveResultId();
      } catch (error) {
        console.error('[PFG Quiz]', error);
        showMessage(error.message || 'We could not determine your result.');
        return;
      }

      state.email = email;
      state.emailCaptured = true;
      state.emailSkipped = false;
      saveState(state);

      if (emailSubmit) {
        emailSubmit.disabled = true;
        emailSubmit.textContent = emailSavingLabel;
      }

      setEmailStatus('', '');

      let subscriptionResult = {
        subscribed: false,
        reason: 'not-attempted'
      };

      try {
        subscriptionResult = await subscribeToKlaviyo(email, resultId);
      } catch (error) {
        console.warn('[PFG Quiz] Klaviyo email capture failed.', error);
        subscriptionResult = {
          subscribed: false,
          reason: 'request-failed'
        };
      }

      document.dispatchEvent(new CustomEvent('pfg:quiz:email-captured', {
        detail: {
          email,
          resultId,
          resultUrl: buildResultUrl(resultId),
          resultTitle: getResultTitle(resultId),
          subscribed: Boolean(subscriptionResult.subscribed),
          answers: { ...state }
        }
      }));

      showResult(resultId);

      if (emailSubmit) {
        emailSubmit.textContent = emailSubmitLabel;
        emailSubmit.disabled = false;
      }
    }

    function skipEmailCapture() {
      state.emailSkipped = true;
      state.emailCaptured = false;
      saveState(state);
      submitQuiz();
    }

    root.addEventListener('click' , (event) => {
      const start = event.target.closest('[data-quiz-start]');
      if (start) {
        showScreen('age');
        return;
      }

      const answer = event.target.closest('[data-quiz-answer]');
      if (answer) {
        if (answer.dataset.question === 'restrictions') {
          chooseRestriction(answer);
        } else {
          chooseSingleAnswer(answer);
        }
        return;
      }

      if (event.target.closest('[data-quiz-next]')) {
        moveForward();
        return;
      }

      if (event.target.closest('[data-quiz-back]')) {
        moveBack();
        return;
      }

      if (event.target.closest('[data-quiz-email-skip]')) {
        skipEmailCapture();
        return;
      }

      if (event.target.closest('[data-quiz-submit]')) {
        submitQuiz();
      }
    });


    if (emailInput) {
      emailInput.addEventListener('input', () => {
        state.email = emailInput.value.trim();
        saveState(state);
        setEmailStatus('', '');
        syncCurrentButtonState();
      });
    }

    if (emailForm) {
      emailForm.addEventListener('submit', (event) => {
        event.preventDefault();
        submitEmailCapture();
      });
    }

    hideAllResults();

    const directResultId = getResultIdFromUrl();

    if (directResultId) {
      showResult(directResultId, {
        updateUrl: false,
        focus: false,
        dispatchCompleted: false
      });
      return;
    }

    const restoredScreen = SCREEN_ORDER.includes(state.currentScreen)
      ? state.currentScreen
      : 'intro';

    showScreen(restoredScreen, { focus: false, persist: false });
  }

  function restartQuiz() {
    clearState();
    updateResultUrl(null);
    delete document.documentElement.dataset.pfgQuizResult;

    document.querySelectorAll('[data-quiz-result]').forEach((result) => {
      result.hidden = true;
      result.classList.remove('is-active');
    });

    const quiz = document.querySelector('[data-main-quiz]');
    if (quiz) {
      quiz.hidden = false;
      initializedRoots.delete(quiz);
      initQuiz(quiz);
      quiz.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-quiz-restart]')) {
      restartQuiz();
    }
  });

  function initAllQuizzes(container = document) {
    container.querySelectorAll('[data-main-quiz]').forEach(initQuiz);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllQuizzes());
  } else {
    initAllQuizzes();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAllQuizzes(event.target);
  });
})();

/* ==========================================================
   PFG QUIZ RESULTS — centralized product, price and cart logic
========================================================== */
(function () {
  if (window.PFGQuizResultCommerceInitialized) return;
  window.PFGQuizResultCommerceInitialized = true;

  var productCache = new Map();

  function normalizeVariantTitle(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\s.\-_/]+/g, '');
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'en-US',
        {
          style: 'currency',
          currency: currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD'
        }
      ).format(Number(cents || 0) / 100);
    } catch (error) {
      return '$' + (Number(cents || 0) / 100).toFixed(2);
    }
  }

  async function getProduct(handle) {
    handle = String(handle || '').replace(/^products\//, '').trim();
    if (!handle) throw new Error('Missing product handle');
    if (productCache.has(handle)) return productCache.get(handle);

    var promise = fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Unable to load product: ' + handle);
      return response.json();
    });

    productCache.set(handle, promise);
    return promise;
  }

  function findVariant(product, node) {
    var variantId = node.getAttribute('data-variant-id');
    if (variantId) {
      return product.variants.find(function (variant) {
        return String(variant.id) === String(variantId);
      });
    }

    var requestedTitle =
      node.getAttribute('data-variant-title') ||
      node.getAttribute('data-variant-price-title');

    if (requestedTitle) {
      var normalizedRequest = normalizeVariantTitle(requestedTitle);
      return product.variants.find(function (variant) {
        var title = normalizeVariantTitle(variant.public_title || variant.title);
        return variant.available && (
          title === normalizedRequest ||
          title.includes(normalizedRequest) ||
          normalizedRequest.includes(title)
        );
      });
    }

    var defaultVariantId = node.getAttribute('data-default-variant-id');
    if (defaultVariantId) {
      var defaultVariant = product.variants.find(function (variant) {
        return String(variant.id) === String(defaultVariantId) && variant.available;
      });
      if (defaultVariant) return defaultVariant;
    }

    return product.variants.find(function (variant) {
      return variant.available;
    }) || product.variants[0];
  }

  async function hydratePrice(node) {
    if (node.dataset.pfgPriceHydrated === 'true') return;
    var handle = node.getAttribute('data-product-price') || node.getAttribute('data-variant-product-price');
    if (!handle) return;

    try {
      var product = await getProduct(handle);
      var variant = findVariant(product, node);
      if (!variant) return;
      node.textContent = formatMoney(variant.price, product.currency);
      node.dataset.pfgPriceHydrated = 'true';
    } catch (error) {
      console.warn('[PFG Quiz] Price unavailable.', handle, error);
    }
  }

  function hydratePrices(root) {
    (root || document)
      .querySelectorAll('[data-product-price], [data-variant-product-price]')
      .forEach(hydratePrice);
  }

  async function addToCart(button) {
    var originalText = button.textContent.trim();
    button.disabled = true;
    button.textContent = 'Adding…';

    try {
      var handle = button.getAttribute('data-product-handle');
      var product = await getProduct(handle);
      var variant = findVariant(product, button);

      if (!variant || !variant.available) {
        throw new Error('Requested variant is unavailable.');
      }

      var response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: Number(variant.id), quantity: 1 }]
        })
      });

      if (!response.ok) throw new Error('Cart request failed.');

      button.textContent = 'Added ✓';
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

      setTimeout(function () {
        button.textContent = originalText;
        button.disabled = false;
      }, 1800);
    } catch (error) {
      console.error('[PFG Quiz]', error);
      button.textContent = 'Try Again';
      button.disabled = false;
    }
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('.pfg-quiz-result .jd-qk-direct-atc');
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    addToCart(button);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      hydratePrices(document);
    });
  } else {
    hydratePrices(document);
  }

  document.addEventListener('pfg:quiz-result-rendered', function (event) {
    hydratePrices(event.detail && event.detail.root ? event.detail.root : document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    hydratePrices(event.target);
  });

  /* Theme editor preview: show the selected quiz result section or block
     immediately while keeping every other result hidden. */
  function hideEditorResults() {
    document.querySelectorAll('[data-quiz-result]').forEach(function (result) {
      result.hidden = true;
      result.classList.remove('is-active');
    });
  }

  function showEditorQuiz() {
    hideEditorResults();
    document.querySelectorAll('[data-main-quiz]').forEach(function (quiz) {
      quiz.hidden = false;
    });
    delete document.documentElement.dataset.pfgQuizResult;
  }

  function findResultBlock(event) {
    var fromTarget = event.target && event.target.closest
      ? event.target.closest('[data-quiz-result]')
      : null;
    if (fromTarget) return fromTarget;

    var blockId = event.detail && event.detail.blockId;
    if (!blockId) return null;
    return document.querySelector('[data-result-block-id="' + String(blockId).replace(/"/g, '\\"') + '"]');
  }

  function findResultSection(event) {
    var target = event.target;

    if (target) {
      if (target.matches && target.matches('[data-quiz-result]')) return target;

      if (target.querySelector) {
        var nestedResult = target.querySelector('[data-quiz-result]');
        if (nestedResult) return nestedResult;
      }

      if (target.closest) {
        var closestResult = target.closest('[data-quiz-result]');
        if (closestResult) return closestResult;
      }
    }

    var sectionId = event.detail && event.detail.sectionId;
    if (!sectionId) return null;

    var sectionWrapper = document.getElementById('shopify-section-' + sectionId);
    return sectionWrapper && sectionWrapper.querySelector
      ? sectionWrapper.querySelector('[data-quiz-result]')
      : null;
  }

  function eventContainsMainQuiz(event) {
    var target = event.target;

    if (target) {
      if (target.matches && target.matches('[data-main-quiz]')) return true;
      if (target.querySelector && target.querySelector('[data-main-quiz]')) return true;
      if (target.closest && target.closest('[data-main-quiz]')) return true;
    }

    var sectionId = event.detail && event.detail.sectionId;
    if (!sectionId) return false;

    var sectionWrapper = document.getElementById('shopify-section-' + sectionId);
    return Boolean(
      sectionWrapper &&
      sectionWrapper.querySelector &&
      sectionWrapper.querySelector('[data-main-quiz]')
    );
  }

  function showEditorResult(result) {
    if (!result) return;

    document.querySelectorAll('[data-main-quiz]').forEach(function (quiz) {
      quiz.hidden = true;
    });

    hideEditorResults();
    result.hidden = false;
    result.classList.add('is-active');
    document.documentElement.dataset.pfgQuizResult = result.dataset.quizResult || '';
    hydratePrices(result);
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:block:select', function (event) {
      showEditorResult(findResultBlock(event));
    });

    document.addEventListener('shopify:block:deselect', function (event) {
      if (findResultBlock(event)) showEditorQuiz();
    });

    document.addEventListener('shopify:section:select', function (event) {
      var selectedResult = findResultSection(event);

      if (selectedResult) {
        showEditorResult(selectedResult);
        return;
      }

      if (eventContainsMainQuiz(event)) {
        showEditorQuiz();
      }
    });
  }
})();
